import { tracked } from "@glimmer/tracking";
import EmberObject, { computed, set } from "@ember/object";
import { trustHTML } from "@ember/template";
import BufferedProxy from "ember-buffered-proxy/proxy";
import {
  DEFAULT_USER_PREFERENCES,
  SITE_SETTING_REQUIRES_CONFIRMATION_TYPES,
} from "discourse/admin/lib/constants";
import SettingObjectHelper from "discourse/admin/lib/setting-object-helper";
import { ajax } from "discourse/lib/ajax";
import { bind } from "discourse/lib/decorators";
import { i18n } from "discourse-i18n";

/**
 * `true` when a value is the *enabled* state of a bool site setting: boolean
 * `true`, the string `"true"`, or any value whose `String()` is exactly `"true"`.
 * This is not general JS truthiness: `"false"` is not enabled, and is safe
 * for `null` / `undefined` (unlike calling `.toString()` on the value).
 */
export function isSettingValueTrue(value) {
  return String(value) === "true";
}

const AUTO_REFRESH_ON_SAVE = [
  "logo",
  "mobile_logo",
  "base_font",
  "heading_font",
  "default_text_size",
];

export default class SiteSetting extends EmberObject {
  static async findAll(params = {}) {
    let settings = await ajax("/admin/site_settings", { data: params });
    const categories = {};
    settings.site_settings.forEach(function (s) {
      if (!categories[s.category]) {
        categories[s.category] = [];
      }
      categories[s.category].push(SiteSetting.create(s));
    });
    return Object.keys(categories).map(function (n) {
      return {
        nameKey: n,
        name: i18n("admin.site_settings.categories." + n),
        siteSettings: categories[n],
      };
    });
  }

  static findByName(name) {
    return ajax("/admin/site_settings", {
      data: {
        names: [name],
      },
    }).then(function (settings) {
      const setting = settings.site_settings.find((s) => s.setting === name);
      return SiteSetting.create(setting);
    });
  }

  static update(key, value, opts = {}) {
    const data = {};
    data[key] = value;

    if (opts["updateExistingUsers"] === true) {
      data["update_existing_user"] = true;
    }

    return ajax(`/admin/site_settings/${key}`, { type: "PUT", data });
  }

  static bulkUpdate(settings) {
    return ajax(`/admin/site_settings/bulk_update.json`, {
      type: "PUT",
      data: { settings },
    });
  }

  @tracked isSaving = false;
  @tracked validationMessage = null;
  updateExistingUsers = false;

  settingObjectHelper = new SettingObjectHelper(this);

  constructor() {
    super(...arguments);
    this.buffered = BufferedProxy.create({ content: this });
  }

  @computed("settingObjectHelper.overridden")
  get overridden() {
    return this.settingObjectHelper?.overridden;
  }

  set overridden(value) {
    set(this, "settingObjectHelper.overridden", value);
  }

  @computed("settingObjectHelper.computedValueProperty")
  get computedValueProperty() {
    return this.settingObjectHelper?.computedValueProperty;
  }

  set computedValueProperty(value) {
    set(this, "settingObjectHelper.computedValueProperty", value);
  }

  @computed("settingObjectHelper.computedNameProperty")
  get computedNameProperty() {
    return this.settingObjectHelper?.computedNameProperty;
  }

  set computedNameProperty(value) {
    set(this, "settingObjectHelper.computedNameProperty", value);
  }

  @computed("settingObjectHelper.validValues")
  get validValues() {
    return this.settingObjectHelper?.validValues;
  }

  set validValues(value) {
    set(this, "settingObjectHelper.validValues", value);
  }

  @computed("settingObjectHelper.allowsNone")
  get allowsNone() {
    return this.settingObjectHelper?.allowsNone;
  }

  set allowsNone(value) {
    set(this, "settingObjectHelper.allowsNone", value);
  }

  @computed("settingObjectHelper.anyValue")
  get anyValue() {
    return this.settingObjectHelper?.anyValue;
  }

  set anyValue(value) {
    set(this, "settingObjectHelper.anyValue", value);
  }

  @computed("setting")
  get staffLogFilter() {
    if (!this.setting) {
      return;
    }

    return {
      subject: this.setting,
      action_name: "change_site_setting",
    };
  }

  get definition() {
    return {
      key: this.setting,
      label: this.humanized_name,
      description: trustHTML(this.description),
      type: this.type,
      list_type: this.list_type,
      min: this.min,
      max: this.max,
      choices: this.choices,
      valid_values: this.validValues,
      allows_none: !!this.allowsNone,
      allow_any: this.allow_any,
      mandatory_values: this.mandatory_values,
      disallowed_groups: this.disallowed_groups,
      currentSavedValue: this.value,
    };
  }

  get pendingValue() {
    return this.buffered.get("value");
  }

  commit() {
    this.validationMessage = null;
    this.buffered.applyChanges();
  }

  rollback() {
    this.buffered.discardChanges();
  }

  get requiresConfirmation() {
    switch (this.requires_confirmation) {
      case SITE_SETTING_REQUIRES_CONFIRMATION_TYPES.simple:
        return true;
      case SITE_SETTING_REQUIRES_CONFIRMATION_TYPES.simple_on_enable: {
        const val = this.buffered?.get("value");
        return isSettingValueTrue(val);
      }
      case SITE_SETTING_REQUIRES_CONFIRMATION_TYPES.simple_on_disable: {
        const val = this.buffered?.get("value");
        return !isSettingValueTrue(val);
      }
      default:
        return false;
    }
  }

  get requiresReload() {
    return AUTO_REFRESH_ON_SAVE.includes(this.setting);
  }

  get affectsExistingUsers() {
    return DEFAULT_USER_PREFERENCES.includes(this.setting);
  }

  @bind
  setUpdateExistingUsers(value) {
    this.updateExistingUsers = value;
  }
}
